<template>
  <div class="client-insights" :class="{ 'client-insights--dark': isDarkSkin }">

    <div class="ci-toolbar">
      <filter-card :filter-key="viewKey" dashboard single-row v-model="filter" v-bind:fields="filterFields"/>
      <!-- One-click department shortcuts: fills the business-unit filter with every unit of that department -->
      <div class="dept-quick" v-if="availableDepartments.length">
        <span class="dept-quick__label">{{ $t('filters.departments') }}</span>
        <button v-for="dept in availableDepartments" :key="dept" type="button"
                class="dept-quick__pill" :class="['dept-quick__pill--' + dept.toLowerCase(), { 'is-active': isDeptActive(dept) }]"
                @click="toggleDept(dept)">
          <i :class="isDeptActive(dept) ? 'fa-solid fa-check-circle' : 'fa-regular fa-circle'"></i>
          {{ $t(`businessUnitDepartments.${dept}`) }}
          <span class="dept-quick__count">{{ deptBuIds(dept).length }}</span>
        </button>
      </div>
      <calendar-bar v-model="dateRange" @input="onFilterChanged"/>
    </div>

    <!-- ============ SEARCH + EXPORT ============ -->
    <div class="ci-controls">
      <!-- Bonus mode: flips the whole leaderboard from real deposits to bonus money -->
      <button type="button" class="ci-bonus-toggle" :class="{ 'is-active': bonusMode }"
              @click="toggleBonusMode">
        <span class="ci-bonus-toggle__track"><span class="ci-bonus-toggle__thumb"></span></span>
        <i class="fas fa-gift"></i>
        {{ $t('clientInsights.bonusMode') }}
      </button>
      <!-- Fake mode: flips the leaderboard to approved deposits marked FAKE
           (mutually exclusive with bonus mode) -->
      <button type="button" class="ci-fake-toggle" :class="{ 'is-active': fakeMode }"
              @click="toggleFakeMode">
        <span class="ci-fake-toggle__track"><span class="ci-fake-toggle__thumb"></span></span>
        <i class="fas fa-mask"></i>
        {{ $t('clientInsights.fakeMode') }}
      </button>
      <!-- TXF mode: flips the leaderboard to TXF (taxes) money
           (mutually exclusive with bonus/fake modes) -->
      <button type="button" class="ci-txf-toggle" :class="{ 'is-active': txfMode }"
              @click="toggleTxfMode">
        <span class="ci-txf-toggle__track"><span class="ci-txf-toggle__thumb"></span></span>
        <i class="fas fa-file-invoice-dollar"></i>
        {{ $t('clientInsights.txfMode') }}
      </button>
      <div class="ci-search">
        <feather-icon icon="SearchIcon" size="14" class="ci-search__icon"/>
        <b-form-input v-model="searchText" :debounce="500" :placeholder="$t('clientInsights.searchClient')"
                      size="sm" class="ci-search__input"/>
        <b-link v-if="searchText" class="ci-search__clear" @click="searchText = ''">
          <feather-icon icon="XIcon" size="13"/>
        </b-link>
      </div>
      <b-button size="sm" class="ci-export" :disabled="exporting" @click="exportExcel">
        <b-spinner v-if="exporting" small class="mr-50"/>
        <i v-else class="fas fa-file-excel mr-50"></i>
        {{ $t('clientInsights.export') }}
      </b-button>

      <button type="button" class="ci-eye" :class="{ 'is-on': hideAmounts }"
              v-b-tooltip :title="hideAmounts ? $t('userInsights.showAmounts') : $t('userInsights.hideAmounts')"
              @click="toggleAmounts">
        <i :class="hideAmounts ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
      </button>
    </div>

    <b-overlay :show="loading" rounded="lg" opacity="0.45" spinner-variant="primary">

      <!-- ============ PODIUM (top 3) ============ -->
      <div class="ci-podium" v-if="topThree.length">
        <div v-for="item in podiumOrder" :key="item.clientId"
             class="ci-podium__card" :class="`ci-podium__card--p${item.rank}`"
             @click="openInsight(item)">
          <div class="ci-podium__medal">
            <span v-if="item.rank === 1">🥇</span>
            <span v-else-if="item.rank === 2">🥈</span>
            <span v-else>🥉</span>
          </div>
          <div class="ci-podium__rank">TOP {{ item.rank }}</div>
          <div class="ci-podium__name">
            {{ item.name }}
            <i v-if="item.vip" class="fas fa-star ci-vip" v-b-tooltip title="VIP"></i>
          </div>
          <div class="ci-podium__country" v-if="item.country">
            <i class="fas fa-location-dot mr-25"></i>{{ item.country }}
          </div>
          <div class="ci-podium__amount" :class="{ 'ci-masked': hideAmounts }">{{ money(item.deposits) }}</div>
          <div class="ci-podium__chips">
            <span class="ci-chip ci-chip--light">
              <i class="fas fa-arrow-down mr-25"></i><span :class="{ 'ci-masked': hideAmounts }">{{ item.depositCount }}</span> {{ $t(countLabel) }}
            </span>
            <span v-if="!modeActive" class="ci-chip ci-chip--light">
              <i class="fas fa-arrow-up mr-25"></i><span :class="{ 'ci-masked': hideAmounts }">{{ money(item.withdrawals) }}</span>
            </span>
            <span v-if="!modeActive" class="ci-chip ci-chip--light">
              {{ $t('clientInsights.net') }}: <span :class="{ 'ci-masked': hideAmounts }">{{ money(item.net) }}</span>
            </span>
          </div>
          <div class="ci-podium__glow"></div>
        </div>
      </div>

      <!-- ============ RANKED LIST (ranks 4+ on page 1, full page otherwise) ============ -->
      <b-card no-body class="ci-list" v-if="restOfList.length">
        <b-card-header class="pb-50">
          <h5 class="mb-0"><i class="fas fa-ranking-star mr-50"></i>{{ $t('clientInsights.leaderboard') }}</h5>
        </b-card-header>
        <b-card-body class="pt-75">
          <div class="ci-tablewrap">
            <div v-for="item in restOfList" :key="item.clientId" class="ci-lb-row"
                 :class="{ 'ci-lb-row--bonus': modeActive, 'ci-lb-row--fakemode': fakeMode, 'ci-lb-row--txfmode': txfMode }" @click="openInsight(item)">
              <div class="ci-lb-row__rank">#{{ item.rank }}</div>
              <div class="ci-lb-row__id">
                <div class="ci-lb-row__avatar" :style="{ background: avatarColor(item.name) }">{{ initials(item.name) }}</div>
                <div class="ci-lb-row__meta">
                  <div class="ci-lb-row__name">
                    {{ item.name }}
                    <i v-if="item.vip" class="fas fa-star ci-vip"></i>
                  </div>
                  <div class="ci-lb-row__sub">
                    <span v-if="item.country"><i class="fas fa-location-dot mr-25"></i>{{ item.country }}</span>
                    <span v-if="item.affiliateName" class="ml-75"><i class="fas fa-handshake mr-25"></i>{{ item.affiliateName }}</span>
                  </div>
                  <div class="ci-lb-row__track"><div class="ci-lb-row__fill" :style="{ width: barWidth(item) }"></div></div>
                </div>
              </div>
              <div class="ci-blk">
                <span class="ci-blk__label">{{ $t(countLabel) }}</span>
                <span class="ci-blk__value" :class="{ 'ci-masked': hideAmounts }">{{ item.depositCount }}</span>
              </div>
              <div v-if="!modeActive" class="ci-blk ci-blk--wd">
                <span class="ci-blk__label">{{ $t('clientInsights.withdrawals') }}</span>
                <span class="ci-blk__value" :class="{ 'ci-masked': hideAmounts }">{{ money(item.withdrawals) }}</span>
              </div>
              <div v-if="!modeActive" class="ci-blk ci-blk--net">
                <span class="ci-blk__label">{{ $t('clientInsights.net') }}</span>
                <span class="ci-blk__value" :class="{ 'ci-masked': hideAmounts }">{{ money(item.net) }}</span>
              </div>
              <!-- Bonus money next to the real-money columns; hidden in bonus /
                   fake mode (the backend nulls it there — the board IS that money).
                   Fake money has NO column here on purpose: it is only visible
                   through fake mode, so the real board stays purely real. -->
              <div v-if="!modeActive" class="ci-blk ci-blk--bonus">
                <span class="ci-blk__label">{{ $t('clientInsights.bonus') }}</span>
                <span class="ci-blk__value" :class="{ 'ci-masked': hideAmounts }">{{ money(item.bonus) }}</span>
              </div>
              <div class="ci-blk ci-blk--total" :class="{ 'ci-blk--total-fake': fakeMode, 'ci-blk--total-txf': txfMode }">
                <span class="ci-blk__label">{{ $t(totalLabel) }}</span>
                <span class="ci-blk__value" :class="{ 'ci-masked': hideAmounts }">{{ money(item.deposits) }}</span>
              </div>
              <div class="ci-lb-row__go"><feather-icon icon="ChevronRightIcon" size="16"/></div>
            </div>
          </div>
        </b-card-body>
      </b-card>

      <!-- ============ PAGINATION ============ -->
      <div class="ci-pager" v-if="top.length && total">
        <b-pagination v-if="total > pageSize" v-model="page" :total-rows="total" :per-page="pageSize"
                      align="center" first-number last-number class="mb-25" @change="onPageChanged"/>
        <div class="ci-pager__meta">
          <b-select style="width: 75px" size="sm" v-model="pageSize" @change="onSizeChanged">
            <b-select-option :value="20">20</b-select-option>
            <b-select-option :value="50">50</b-select-option>
            <b-select-option :value="100">100</b-select-option>
          </b-select>
          <span class="ci-pager__summary">{{ top.length }} {{ $t('clientInsights.showingOf') }} {{ total }}</span>
        </div>
      </div>

      <div v-if="!loading && !top.length" class="ci-empty">
        <i class="fas fa-crown ci-empty__icon"></i>
        <div class="ci-empty__title">{{ $t(emptyTitle) }}</div>
        <div class="ci-empty__hint">{{ $t('clientInsights.noDataHint') }}</div>
      </div>

    </b-overlay>

    <client-insight-modal ref="insightModal"/>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import CalendarBar from '@/components/widget/CalendarBar';
import FilterCard from '@/components/widget/FilterCard';
import mixinBase from '@/components/mixin/mixinBase';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import mixinPermissions from '@/components/mixin/mixinPermissions';
import ClientInsightsFilterFields from '@/model/filter/ClientInsightsFilterFields';
import ClientInsightModal from '@/components/modal/ClientInsightModal';

const REFRESH_DEBOUNCE_MS = 250;

export default {
  name: 'ClientInsights',
  mixins: [mixinBase, mixinNotifications, mixinPermissions],
  components: { CalendarBar, FilterCard, ClientInsightModal },
  data: () => ({
    filter: { orgIds: [], buIds: [], userIds: [], countries: [], affiliateIds: [], statusIds: [] },
    filterFields: [],
    dateRange: { from: null, until: null },
    loading: false,
    refreshTimer: null,
    top: [],
    searchText: '',
    page: 1,
    pageSize: 20,
    total: 0,
    exporting: false,
    // Bonus mode: leaderboard ranks by bonus money (CREDIT/BONUS) instead of real deposits
    bonusMode: false,
    // Fake mode: leaderboard ranks by approved deposits marked FAKE (mutually exclusive with bonus mode)
    fakeMode: false,
    // TXF mode: leaderboard ranks by TXF (taxes) money (mutually exclusive with the other modes)
    txfMode: false,
    // Privacy eye: default OPEN — click blurs the figures on demand.
    hideAmounts: false,
  }),
  watch: {
    filter: { handler: 'onFilterChanged', deep: true },
    // b-form-input :debounce="500" delays the model update, so this fires once typing settles
    searchText: 'onFilterChanged',
  },
  computed: {
    ...mapGetters('appConfig', ['isDarkSkin']),
    ...mapGetters('data', ['allBusinessUnits']),
    viewKey() { return this.$route.name || 'client-insights'; },
    // Departments that actually have business units (SALES / RETENTION / LANDING).
    availableDepartments() {
      const present = new Set((this.allBusinessUnits || []).map(b => b.type).filter(Boolean));
      return ['SALES', 'RETENTION', 'LANDING'].filter(t => present.has(t));
    },
    // Podium is only meaningful on the first, unfiltered-by-search page
    showPodium() { return this.page === 1 && !this.searchText; },
    topThree() { return this.showPodium ? this.top.slice(0, 3) : []; },
    restOfList() { return this.showPodium ? this.top.slice(3) : this.top; },
    // Classic podium arrangement: 2nd - 1st - 3rd
    podiumOrder() {
      const three = this.topThree;
      if (three.length < 3) return three;
      return [three[1], three[0], three[2]];
    },
    // Backend sorts by deposits desc, so row 0 is the max of the current page (podium shown or not)
    maxDeposit() { return this.top.length ? this.top[0].deposits : 0; },
    // A single-money board (bonus / fake): withdrawals / net / bonus / fake
    // tiles are hidden — the backend nulls them since the board IS that money
    modeActive() { return this.bonusMode || this.fakeMode || this.txfMode; },
    countLabel() {
      if (this.bonusMode) return 'clientInsights.bonuses';
      if (this.fakeMode) return 'clientInsights.fakes';
      if (this.txfMode) return 'clientInsights.txfs';
      return 'clientInsights.deposits';
    },
    totalLabel() {
      if (this.bonusMode) return 'clientInsights.totalBonus';
      if (this.fakeMode) return 'clientInsights.totalFake';
      if (this.txfMode) return 'clientInsights.totalTxf';
      return 'clientInsights.totalDeposited';
    },
    emptyTitle() {
      if (this.bonusMode) return 'clientInsights.noBonusData';
      if (this.fakeMode) return 'clientInsights.noFakeData';
      if (this.txfMode) return 'clientInsights.noTxfData';
      return 'clientInsights.noData';
    },
  },
  created() {
    this.filterFields = ClientInsightsFilterFields();
    this.dateRange = {
      from: this.$moment().startOf('month').format('YYYY-MM-DD'),
      until: this.$moment().endOf('month').format('YYYY-MM-DD'),
    };
  },
  mounted() { this.$root.$on('dashboard::refresh', this.scheduleRefresh); },
  beforeDestroy() {
    this.$root.$off('dashboard::refresh', this.scheduleRefresh);
    if (this.refreshTimer) { clearTimeout(this.refreshTimer); this.refreshTimer = null; }
  },
  methods: {
    ...mapActions('dashboard', ['getClientInsightsTop', 'downloadInsightsExcel']),

    // ---- Department quick-pick: one click selects ALL units of a department ----
    deptBuIds(dept) {
      let bus = (this.allBusinessUnits || []).filter(b => b.type === dept);
      const orgs = this.filter.orgIds || [];
      if (orgs.length) {
        bus = bus.filter(b => orgs.includes(b.orgId));
      }
      return bus.map(b => b.id);
    },
    isDeptActive(dept) {
      const ids = this.deptBuIds(dept);
      const sel = this.filter.buIds || [];
      return ids.length > 0 && ids.every(id => sel.includes(id));
    },
    toggleDept(dept) {
      const ids = this.deptBuIds(dept);
      if (!ids.length) return;
      const sel = new Set(this.filter.buIds || []);
      if (this.isDeptActive(dept)) {
        ids.forEach(id => sel.delete(id));
      } else {
        ids.forEach(id => sel.add(id));
      }
      this.filter.buIds = [...sel]; // deep watcher -> onFilterChanged -> refresh
    },

    money(v) { return this.convertToEURO(v || 0); },
    toggleAmounts() {
      this.hideAmounts = !this.hideAmounts;
    },
    // Mode switches: full refetch from page 1 — the backend swaps the matched
    // transactions (real / bonus / fake / txf), so the data set is different.
    // The modes are mutually exclusive: switching one on drops the others.
    toggleBonusMode() {
      this.bonusMode = !this.bonusMode;
      if (this.bonusMode) { this.fakeMode = false; this.txfMode = false; }
      this.onFilterChanged();
    },
    toggleFakeMode() {
      this.fakeMode = !this.fakeMode;
      if (this.fakeMode) { this.bonusMode = false; this.txfMode = false; }
      this.onFilterChanged();
    },
    toggleTxfMode() {
      this.txfMode = !this.txfMode;
      if (this.txfMode) { this.bonusMode = false; this.fakeMode = false; }
      this.onFilterChanged();
    },
    barWidth(item) {
      if (!this.maxDeposit) return '0%';
      return Math.max(3, Math.round((item.deposits / this.maxDeposit) * 100)) + '%';
    },
    scheduleRefresh() {
      if (this.refreshTimer) clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refresh(), REFRESH_DEBOUNCE_MS);
    },
    // Any change of filter, date range or search restarts from page 1
    onFilterChanged() {
      this.page = 1;
      this.scheduleRefresh();
    },
    // Fired only on user interaction (v-model handles the value); programmatic page
    // resets go through onFilterChanged, and scheduleRefresh coalesces overlapping
    // triggers into a single fetch, so no double request is issued.
    onPageChanged(page) {
      this.page = page;
      this.scheduleRefresh();
    },
    // Page-size select: restart from page 1 with the new size
    onSizeChanged() {
      this.page = 1;
      this.scheduleRefresh();
    },
    refresh() {
      if (!this.dateRange.from) return;
      this.loading = true;
      const payload = {
        ...this.filter,
        range: { from: this.dateRange.from, until: this.dateRange.until },
        search: this.searchText || null,
        bonusMode: this.bonusMode || null,
        fakeMode: this.fakeMode || null,
        txfMode: this.txfMode || null,
        page: this.page,
        size: this.pageSize,
      };
      this.getClientInsightsTop(payload)
          .then(res => {
            this.top = (res && res.data) || [];
            this.total = (res && res.pagination && res.pagination.total) || 0;
          })
          .catch(err => this.showServerErrorNotification('getClientInsightsTop', 'clientInsights', err))
          .finally(() => { this.loading = false; });
    },
    exportExcel() {
      if (this.exporting) return;
      this.exporting = true;
      // The export receives the mode flags too, so the Excel mirrors the on-screen board
      const filter = {
        ...this.filter,
        range: { from: this.dateRange.from, until: this.dateRange.until },
        search: this.searchText || null,
        bonusMode: this.bonusMode || null,
        fakeMode: this.fakeMode || null,
        txfMode: this.txfMode || null,
      };
      this.downloadInsightsExcel({ kind: 'clients', filter })
          .catch(err => this.showServerErrorNotification('downloadInsightsExcel', 'clientInsights', err))
          .finally(() => { this.exporting = false; });
    },
    openInsight(item) {
      // Pass the page's date range; the modal has its own calendar to change it
      this.$refs.insightModal.show(item.clientId, item.rank,
          { from: this.dateRange.from, until: this.dateRange.until });
    },
    initials(name) {
      if (!name) return '?';
      const parts = name.trim().split(/\s+/).filter(Boolean);
      if (parts.length === 0) return '?';
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },
    avatarColor(name) {
      const palette = ['#007BFF', '#28c76f', '#00cfe8', '#ff9f43', '#ea5455', '#6e6b7b', '#ff6f91', '#3fbff7'];
      if (!name) return palette[0];
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
      }
      return palette[hash % palette.length];
    },
  },
};
</script>

<style scoped lang="scss">
.client-insights {
  .ci-toolbar { display: flex; flex-direction: column; gap: 0.5rem; margin-bottom: 1rem; }
}

/* ===== Search + export controls ===== */
.ci-controls {
  display: flex; align-items: center; justify-content: flex-end;
  gap: 0.75rem; flex-wrap: wrap; margin-bottom: 1rem;
}

.ci-search {
  position: relative; display: inline-flex; align-items: center; min-width: 260px;

  &__icon { position: absolute; left: 10px; color: #9a9dad; pointer-events: none; z-index: 1; }
  &__input { padding-left: 30px; padding-right: 26px; border-radius: 10px; }
  &__clear {
    position: absolute; right: 8px; display: inline-flex; color: #9a9dad;
    &:hover { color: #6e6b7b; }
  }
}

.ci-export {
  border: none !important;
  background: linear-gradient(150deg, #2ecb71 0%, #1e9e5a 55%, #147a45 100%) !important;
  color: #fff !important;
  font-weight: 700;
  border-radius: 10px;
  box-shadow: 0 8px 18px -10px rgba(20, 122, 69, 0.65);
  transition: transform 0.18s ease, box-shadow 0.18s ease;

  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 12px 22px -10px rgba(20, 122, 69, 0.75); }
  &:disabled { opacity: 0.65; }
}

/* Privacy eye — banking-app style toggle; unhiding asks for the password */
.ci-eye {
  display: inline-flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; flex-shrink: 0;
  border: 1px solid rgba(110, 117, 159, 0.24); border-radius: 999px;
  background: #fff; color: #5e5873; cursor: pointer; font-size: 0.85rem;
  transition: color 0.2s ease, border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;

  &:hover { border-color: #7367f0; color: #7367f0; }
  &.is-on {
    background: #7367f0; border-color: #7367f0; color: #fff;
    box-shadow: 0 8px 18px -10px rgba(115, 103, 240, 0.8);
  }
}

/* Applied to every masked figure while the eye is on */
.ci-masked {
  filter: blur(7px);
  user-select: none;
  pointer-events: none;
}

/* Bonus-mode pill toggle — same premium pill language as the dept quick picks,
   in the bonus magenta of the .ci-blk--bonus stat block */
.ci-bonus-toggle {
  display: inline-flex; align-items: center; gap: 0.45rem;
  padding: 0.38rem 0.85rem; min-height: 32px;
  border-radius: 999px;
  border: 1px solid rgba(232, 62, 140, 0.3);
  background: rgba(232, 62, 140, 0.06);
  color: #e83e8c; font-size: 0.8rem; font-weight: 700;
  cursor: pointer; outline: none; white-space: nowrap;
  transition: all 0.18s ease;

  &:hover { background: rgba(232, 62, 140, 0.13); }

  &__track {
    position: relative; width: 26px; height: 14px; border-radius: 999px;
    background: rgba(232, 62, 140, 0.28);
    transition: background 0.18s ease;
  }
  &__thumb {
    position: absolute; top: 2px; left: 2px; width: 10px; height: 10px;
    border-radius: 50%; background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
    transition: transform 0.18s ease;
  }

  &.is-active {
    color: #fff;
    border-color: transparent;
    background: linear-gradient(135deg, #ff6f91 0%, #e83e8c 55%, #c2186b 100%);
    box-shadow: 0 6px 14px -6px rgba(232, 62, 140, 0.55);

    .ci-bonus-toggle__track { background: rgba(255, 255, 255, 0.35); }
    .ci-bonus-toggle__thumb { transform: translateX(12px); }
  }
}

/* Fake-mode pill toggle — same pill language as the bonus toggle, in the
   amber FAKE treatment used across the dashboards */
.ci-fake-toggle {
  display: inline-flex; align-items: center; gap: 0.45rem;
  padding: 0.38rem 0.85rem; min-height: 32px;
  border-radius: 999px;
  border: 1px solid rgba(255, 159, 67, 0.4);
  background: rgba(255, 159, 67, 0.08);
  color: #e87722; font-size: 0.8rem; font-weight: 700;
  cursor: pointer; outline: none; white-space: nowrap;
  transition: all 0.18s ease;

  &:hover { background: rgba(255, 159, 67, 0.16); }

  &__track {
    position: relative; width: 26px; height: 14px; border-radius: 999px;
    background: rgba(255, 159, 67, 0.35);
    transition: background 0.18s ease;
  }
  &__thumb {
    position: absolute; top: 2px; left: 2px; width: 10px; height: 10px;
    border-radius: 50%; background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
    transition: transform 0.18s ease;
  }

  &.is-active {
    color: #fff;
    border-color: transparent;
    background: linear-gradient(135deg, #ffb75d 0%, #ff9f43 55%, #e8590c 100%);
    box-shadow: 0 6px 14px -6px rgba(240, 134, 43, 0.55);

    .ci-fake-toggle__track { background: rgba(255, 255, 255, 0.35); }
    .ci-fake-toggle__thumb { transform: translateX(12px); }
  }
}

/* TXF-mode pill toggle — same pill language, in the teal TXF treatment
   used across the dashboards */
.ci-txf-toggle {
  display: inline-flex; align-items: center; gap: 0.45rem;
  padding: 0.38rem 0.85rem; min-height: 32px;
  border-radius: 999px;
  border: 1px solid rgba(20, 184, 166, 0.4);
  background: rgba(20, 184, 166, 0.08);
  color: #0f9488; font-size: 0.8rem; font-weight: 700;
  cursor: pointer; outline: none; white-space: nowrap;
  transition: all 0.18s ease;

  &:hover { background: rgba(20, 184, 166, 0.16); }

  &__track {
    position: relative; width: 26px; height: 14px; border-radius: 999px;
    background: rgba(20, 184, 166, 0.35);
    transition: background 0.18s ease;
  }
  &__thumb {
    position: absolute; top: 2px; left: 2px; width: 10px; height: 10px;
    border-radius: 50%; background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
    transition: transform 0.18s ease;
  }

  &.is-active {
    color: #fff;
    border-color: transparent;
    background: linear-gradient(135deg, #2dd4bf 0%, #14b8a6 55%, #0f766e 100%);
    box-shadow: 0 6px 14px -6px rgba(15, 118, 110, 0.55);

    .ci-txf-toggle__track { background: rgba(255, 255, 255, 0.35); }
    .ci-txf-toggle__thumb { transform: translateX(12px); }
  }
}

/* ===== Podium ===== */
.ci-podium {
  display: grid;
  grid-template-columns: 1fr 1.25fr 1fr;
  gap: 1rem;
  align-items: end;
  margin-bottom: 1.25rem;

  &__card {
    position: relative;
    border-radius: 20px;
    padding: 1.4rem 1.25rem 1.25rem;
    color: #fff;
    cursor: pointer;
    overflow: hidden;
    text-align: center;
    box-shadow: 0 14px 30px -16px rgba(20, 24, 50, 0.35);
    transition: transform 0.22s ease, box-shadow 0.22s ease;

    &:hover { transform: translateY(-5px); box-shadow: 0 20px 44px -18px rgba(20, 24, 50, 0.45); }

    &--p1 {
      background: linear-gradient(150deg, #f6c744 0%, #eda912 55%, #c98a00 100%);
      padding-top: 2rem;
      padding-bottom: 1.8rem;
    }
    &--p2 { background: linear-gradient(150deg, #b8c2d8 0%, #8e9bb5 55%, #6e7c99 100%); }
    &--p3 { background: linear-gradient(150deg, #e2a06c 0%, #c97e42 55%, #a75f28 100%); }
  }

  &__medal { font-size: 2.2rem; line-height: 1; margin-bottom: 0.35rem; }
  &__rank { font-size: 0.72rem; font-weight: 800; letter-spacing: 0.14em; opacity: 0.85; }
  &__name {
    font-size: 1.15rem; font-weight: 700; margin-top: 0.3rem;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  &__country { font-size: 0.78rem; opacity: 0.85; margin-top: 0.15rem; }
  &__amount { font-size: 1.7rem; font-weight: 800; margin-top: 0.6rem; letter-spacing: -0.01em; }
  &__chips { display: flex; justify-content: center; gap: 0.4rem; margin-top: 0.7rem; flex-wrap: wrap; }
  &__glow {
    position: absolute; top: -45%; right: -18%; width: 240px; height: 240px; border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.28), transparent 70%); pointer-events: none;
  }
}

.ci-chip {
  display: inline-flex; align-items: center;
  padding: 0.22rem 0.6rem; border-radius: 999px;
  font-size: 0.72rem; font-weight: 700;
  &--light { background: rgba(255, 255, 255, 0.22); color: #fff; backdrop-filter: blur(4px); }
}

.ci-vip { color: #ffd54a; font-size: 0.8rem; margin-left: 0.2rem; text-shadow: 0 1px 3px rgba(0,0,0,0.3); }

/* ===== Ranked list: elevated card rows — same design language as the podium
   and KPI tiles (soft shadows, tinted stat blocks, gradient total) ===== */
.ci-list { border-radius: 16px; background: transparent; box-shadow: none; border: none; }
.ci-tablewrap { overflow-x: auto; padding: 2px; }

.ci-lb-row {
  display: grid;
  /* rank | client | count | withdrawals | net | bonus | total | chevron */
  grid-template-columns: 48px minmax(240px, 1fr) 105px 145px 145px 130px 185px 30px;
  gap: 0 0.8rem;
  align-items: center;
  min-width: 1140px;
  background: #fff;
  border: 1px solid rgba(110, 117, 159, 0.1);
  border-radius: 14px;
  padding: 0.7rem 0.9rem;
  margin-bottom: 0.6rem;
  cursor: pointer;
  box-shadow: 0 6px 16px -12px rgba(20, 24, 50, 0.18);
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(115, 103, 240, 0.35);
    box-shadow: 0 14px 30px -14px rgba(115, 103, 240, 0.4);

    .ci-lb-row__go { background: #7367f0; color: #fff; }
  }

  &__rank {
    width: 40px; height: 40px; border-radius: 12px;
    display: inline-flex; align-items: center; justify-content: center;
    background: linear-gradient(135deg, rgba(115, 103, 240, 0.16), rgba(115, 103, 240, 0.06));
    color: #7367f0; font-weight: 800; font-size: 0.82rem;
  }
  &__id { display: flex; align-items: center; gap: 0.7rem; min-width: 0; }
  &__avatar {
    width: 40px; height: 40px; border-radius: 50%; flex-shrink: 0;
    display: inline-flex; align-items: center; justify-content: center;
    color: #fff; font-weight: 800; font-size: 0.85rem;
    box-shadow: 0 4px 10px -4px rgba(20, 24, 50, 0.4);
  }
  &__meta { min-width: 0; flex: 1; }
  &__name { font-weight: 700; color: #5e5873; font-size: 0.95rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  &__sub { font-size: 0.72rem; color: #9a9dad; margin-top: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  &__track {
    margin-top: 5px; height: 5px; width: 100%;
    border-radius: 999px; background: rgba(110, 117, 159, 0.14); overflow: hidden;
  }
  &__fill {
    height: 100%; border-radius: 999px;
    background: linear-gradient(90deg, #7367f0, #9e95f5);
    transition: width 0.5s ease;
  }
  &__go {
    width: 26px; height: 26px; border-radius: 50%;
    display: inline-flex; align-items: center; justify-content: center;
    background: rgba(110, 117, 159, 0.1); color: #9a9dad;
    transition: background 0.18s ease, color 0.18s ease;
    justify-self: end;
  }

  /* Bonus / fake mode: withdrawals / net / bonus / fake blocks are hidden, so
     the grid collapses to rank | client | count | total | chevron */
  &--bonus {
    grid-template-columns: 48px minmax(240px, 1fr) 105px 185px 30px;
    min-width: 700px;
  }

  /* Fake mode: the progress bar goes amber so the board can't be misread as real money */
  &--fakemode .ci-lb-row__fill {
    background: linear-gradient(90deg, #ff9f43, #ffb75d);
  }

  /* TXF mode: teal, the TXF treatment used across the dashboards */
  &--txfmode .ci-lb-row__fill {
    background: linear-gradient(90deg, #14b8a6, #2dd4bf);
  }
}

/* Mini KPI blocks inside each row — label on top, bold value below */
.ci-blk {
  display: flex; flex-direction: column; align-items: flex-end;
  border-radius: 10px; padding: 0.4rem 0.7rem; line-height: 1.25;
  background: rgba(110, 117, 159, 0.07);

  &__label {
    font-size: 0.58rem; font-weight: 800; text-transform: uppercase;
    letter-spacing: 0.08em; color: #9a9dad; white-space: nowrap;
  }
  &__value {
    font-size: 1rem; font-weight: 800; color: #6e6b7b;
    font-variant-numeric: tabular-nums; white-space: nowrap;
  }
  &--wd { background: rgba(234, 84, 85, 0.09);
    .ci-blk__label { color: rgba(234, 84, 85, 0.75); }
    .ci-blk__value { color: #ea5455; }
  }
  &--net { background: rgba(40, 199, 111, 0.1);
    .ci-blk__label { color: rgba(33, 160, 89, 0.85); }
    .ci-blk__value { color: #28c76f; }
  }
  &--bonus { background: rgba(232, 62, 140, 0.09);
    .ci-blk__label { color: rgba(232, 62, 140, 0.75); }
    .ci-blk__value { color: #e83e8c; }
  }
  &--total {
    background: linear-gradient(135deg, #7367f0, #5e50ee);
    box-shadow: 0 6px 14px -6px rgba(115, 103, 240, 0.55);
    .ci-blk__label { color: rgba(255, 255, 255, 0.8); }
    .ci-blk__value { color: #fff; font-size: 1.12rem; }
  }

  /* Fake mode: the gradient total goes amber — it's fake money, not real */
  &--total-fake {
    background: linear-gradient(135deg, #ffb75d, #e8590c);
    box-shadow: 0 6px 14px -6px rgba(232, 89, 12, 0.55);
  }

  /* TXF mode: the gradient total goes teal — taxes money */
  &--total-txf {
    background: linear-gradient(135deg, #2dd4bf, #0f766e);
    box-shadow: 0 6px 14px -6px rgba(15, 118, 110, 0.55);
  }
}

/* ===== Pagination ===== */
.ci-pager {
  display: flex; flex-direction: column; align-items: center;
  margin-top: 1rem;
  &__meta { display: flex; align-items: center; gap: 0.55rem; margin-top: 0.15rem; }
  &__summary { font-size: 0.78rem; color: #9a9dad; font-variant-numeric: tabular-nums; }
}

/* ===== Empty state ===== */
.ci-empty {
  text-align: center; padding: 3.5rem 1rem; color: #9a9dad;
  &__icon { font-size: 2.4rem; opacity: 0.5; }
  &__title { font-weight: 700; margin-top: 0.8rem; font-size: 1.05rem; }
  &__hint { font-size: 0.82rem; margin-top: 0.2rem; }
}

/* ===== Dark mode ===== */
.client-insights--dark {
  .ci-eye {
    background: #283046; border-color: rgba(255, 255, 255, 0.12); color: #b4b7bd;
    &:hover { border-color: #9e95f5; color: #9e95f5; }
    &.is-on { background: #7367f0; border-color: #7367f0; color: #fff; }
  }
  .ci-lb-row {
    background: #283046;
    border-color: rgba(255, 255, 255, 0.06);
    box-shadow: 0 8px 20px -14px rgba(0, 0, 0, 0.6);
    &:hover { border-color: rgba(158, 149, 245, 0.4); }
    &__name { color: #d0d2d6; }
    &__track { background: rgba(255, 255, 255, 0.1); }
    &__go { background: rgba(255, 255, 255, 0.08); }
  }
  .ci-blk {
    background: rgba(255, 255, 255, 0.05);
    .ci-blk__value { color: #d0d2d6; }
    &--wd { background: rgba(234, 84, 85, 0.14); .ci-blk__value { color: #ff8a8b; } }
    &--net { background: rgba(40, 199, 111, 0.14); .ci-blk__value { color: #48da8c; } }
    &--bonus { background: rgba(232, 62, 140, 0.16); .ci-blk__value { color: #f77eb9; } }
    &--total { background: linear-gradient(135deg, #7367f0, #5e50ee); .ci-blk__value { color: #fff; } }
  }
  .ci-bonus-toggle {
    border-color: rgba(232, 62, 140, 0.45);
    background: rgba(232, 62, 140, 0.12);
    color: #f77eb9;
    &:hover { background: rgba(232, 62, 140, 0.2); }
    &.is-active {
      color: #fff;
      border-color: transparent;
      background: linear-gradient(135deg, #ff6f91 0%, #e83e8c 55%, #c2186b 100%);
    }
  }
  .ci-fake-toggle {
    border-color: rgba(255, 159, 67, 0.5);
    background: rgba(255, 159, 67, 0.14);
    color: #ffb054;
    &:hover { background: rgba(255, 159, 67, 0.22); }
    &.is-active {
      color: #fff;
      border-color: transparent;
      background: linear-gradient(135deg, #ffb75d 0%, #ff9f43 55%, #e8590c 100%);
    }
  }
  .ci-search__clear:hover { color: #d0d2d6; }
}

@media (max-width: 992px) {
  .ci-podium { grid-template-columns: 1fr; align-items: stretch;
    &__card--p1 { order: -1; }
  }
  /* Narrow screens: the grid keeps its shape and scrolls horizontally */
  .ci-controls { justify-content: stretch;
    .ci-search { flex: 1; }
  }
}

/* ===== Department quick pills (same look as the sales dashboard) ===== */
.dept-quick {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  padding: 0.6rem 0.85rem;
  background: linear-gradient(135deg, rgba(0, 123, 255, 0.06), rgba(0, 123, 255, 0.02));
  border: 1px solid rgba(0, 123, 255, 0.12);
  border-radius: 14px;

  &__label {
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #b4b7bd;
    padding-right: 0.5rem;
    border-right: 1px solid rgba(110, 117, 159, 0.18);
    margin-right: 0.35rem;
  }

  &__pill {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.38rem 0.85rem;
    min-height: 32px;
    border-radius: 999px;
    border: 1px solid transparent;
    background: transparent;
    color: #6e6b7b;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    outline: none;
    white-space: nowrap;
    transition: all 0.18s ease;

    &--sales:hover { background: rgba(16, 185, 129, 0.1); color: #10b981; }
    &--retention:hover { background: rgba(0, 123, 255, 0.1); color: #007BFF; }
    &--landing:hover { background: rgba(245, 158, 11, 0.12); color: #d97706; }

    &--sales.is-active {
      color: #fff;
      background: linear-gradient(135deg, #34d399 0%, #10b981 60%, #059669 100%);
      box-shadow: 0 6px 14px -6px rgba(16, 185, 129, 0.55);
    }
    &--retention.is-active {
      color: #fff;
      background: linear-gradient(135deg, #4da3ff 0%, #007BFF 60%, #0062cc 100%);
      box-shadow: 0 6px 14px -6px rgba(0, 123, 255, 0.55);
    }
    &--landing.is-active {
      color: #fff;
      background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 60%, #d97706 100%);
      box-shadow: 0 6px 14px -6px rgba(245, 158, 11, 0.55);
    }
  }

  &__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 999px;
    background: rgba(110, 117, 159, 0.14);
    font-size: 0.68rem;
    font-weight: 700;
  }
  &__pill.is-active .dept-quick__count { background: rgba(0, 0, 0, 0.16); }
}

.client-insights--dark .dept-quick {
  background: linear-gradient(135deg, rgba(77, 163, 255, 0.10), rgba(77, 163, 255, 0.02));
  border-color: rgba(77, 163, 255, 0.18);

  .dept-quick__label { border-right-color: rgba(255, 255, 255, 0.08); }
  .dept-quick__pill { color: #b4b7bd; }
  .dept-quick__count { background: rgba(255, 255, 255, 0.1); }
}
</style>
